import type WaSelect from '@awesome.me/webawesome-pro/dist/components/select/select.js'
import { type Cleanup, composeCleanup } from '../helpers/cleanup'
import { triggerForm } from '../helpers/utils'
import { setupPageComponent } from '../helpers/webawesome-components'

// Private types ---------------------------------------------------------------

type SelectHook = {
  readonly el: HTMLElement
  selectCleanup?: Cleanup
}

// Constants -------------------------------------------------------------------

const POPUP_DISTANCE_PX = 4

// Private functions -----------------------------------------------------------

// Recognizes the select host whose definition is ready before setup.
const isWaSelect = (element: HTMLElement): element is WaSelect =>
  element.localName === 'wa-select'

// Aligns the popup after its first render while this hook remains active.
const setupPopupDistance = (element: HTMLElement): (() => void) => {
  if (!isWaSelect(element)) {
    return () => {}
  }

  let active = true
  Promise.resolve(element.updateComplete)
    .then(() => {
      if (active && element.popup.distance !== POPUP_DISTANCE_PX) {
        element.popup.distance = POPUP_DISTANCE_PX
        element.popup.reposition()
      }
    })
    .catch(() => undefined)

  return () => {
    active = false
  }
}

// Marks an autosaved change pending until LiveView acknowledges it.
const setupBusyState = (element: HTMLElement): (() => void) => {
  const handleChange = (): void => {
    element.setAttribute('aria-busy', 'true')
  }

  element.addEventListener('input', handleChange, true)
  element.addEventListener('change', handleChange, true)

  return () => {
    element.removeEventListener('input', handleChange, true)
    element.removeEventListener('change', handleChange, true)
  }
}

// Keeps native form recovery and submission aligned with the visible selection.
const setupFormCarrier = (element: HTMLElement): Cleanup => {
  const carrier = element.parentElement?.querySelector('[data-select-value]')

  if (!(isWaSelect(element) && carrier instanceof window.HTMLSelectElement)) {
    return () => undefined
  }

  const relay = (event: Event): void => {
    if (event.target !== element) {
      return
    }

    event.stopPropagation()
    const values = Array.isArray(element.value)
      ? element.value
      : [element.value]
    for (const option of carrier.options) {
      option.selected = values.includes(option.value)
    }
    triggerForm(
      carrier.id,
      event.type === 'input' ? 'input' : 'change',
      undefined,
      element
    )
  }

  element.addEventListener('input', relay)
  element.addEventListener('change', relay)

  return () => {
    element.removeEventListener('input', relay)
    element.removeEventListener('change', relay)
  }
}

// Attaches autosave feedback and placement to one upgraded select.
const setupSelect = (element: HTMLElement): Cleanup =>
  composeCleanup(
    setupFormCarrier(element),
    element.hasAttribute('phx-change') || element.closest('form[phx-change]')
      ? setupBusyState(element)
      : () => undefined,
    setupPopupDistance(element)
  )

// Public hook -----------------------------------------------------------------

export const FacadeSelect = {
  // Defers setup until the select can expose its popup API.
  mounted(this: SelectHook): void {
    this.selectCleanup?.()
    this.selectCleanup = setupPageComponent(this.el, setupSelect)
  },

  // Releases active setup and cancels imports that complete after navigation.
  destroyed(this: SelectHook): void {
    this.selectCleanup?.()
    this.selectCleanup = undefined
  }
}
